/**
 * What a revocation will remove, and what it will leave alone — shown BEFORE the operator
 * commits, not reported afterwards.
 *
 * THE REQUIREMENT THIS EXISTS FOR: revoking a learning path must not remove a course the
 * learner owns independently — bought separately, granted by a different path, or already held
 * before this order was fulfilled. The backend decides that (`revocation.plan`, and the same
 * algebra runs again inside the write transaction). This component's whole job is to SHOW the
 * decision, split into two lists, with the server's own sentence against each row.
 *
 * WHY THE SERVER'S SENTENCE AND NOT OUR OWN. `note` carries the other order's id, and for a
 * path-held course the parent path's key. "Access kept -- the learner holds this independently
 * through order ord_xyz (via path-v1:...)" is a fact an operator can go and check. Any local
 * paraphrase loses the id, and the id is the only part that settles an argument.
 *
 * WHY THIS IS THE PREVIEW, and its limit, stated honestly: there is no dry-run endpoint for a
 * paid refund. `GET orders/<ref>` returns the plan read-only, so the plan the operator reads
 * IS the server's plan — but it was computed when the screen loaded, and the server re-derives
 * it inside the transaction. `version` (optimistic concurrency on the order) is what stops a
 * stale screen acting on a plan that has since changed; a mismatch is refused with
 * `stale_order_version` rather than silently widening what is revoked.
 */

import { Badge, Banner, DataTable } from '../../shared/ui';
import type { Column } from '../../shared/ui';
import { OPS_COPY, type WireGrantDecision } from '../api/paymentOps';

/** A decision is either "this goes" or "this stays". Nothing here is a third thing. */
export function partitionPlan(decisions: readonly WireGrantDecision[]) {
  const revoked = decisions.filter((row) => row.decision === 'revoked');
  const retained = decisions.filter((row) => row.decision !== 'revoked');
  return { revoked, retained };
}

const RETENTION_LABEL: Record<string, string> = {
  superseded_by_other_grant: 'Held independently',
  retained_pre_existing: 'Held before this order',
};

function itemColumns(): Column<WireGrantDecision>[] {
  return [
    {
      key: 'target',
      header: 'Item',
      mono: true,
      render: (row) => (
        <span>
          {row.target_key}
          {/* A member course names its parent path, so an operator can see WHY it is in this
              list at all. Absent for a directly purchased item — an em dash would imply the
              path was missing rather than inapplicable. */}
          {row.parent_target_key ? (
            <>
              <br />
              <small className="admin-muted">in {row.parent_target_key}</small>
            </>
          ) : null}
        </span>
      ),
    },
    {
      key: 'kind',
      header: 'Type',
      render: (row) => <Badge tone="neutral">{row.target_kind || 'unknown'}</Badge>,
    },
    { key: 'note', header: 'Why', render: (row) => row.note },
  ];
}

export function GrantPlan({
  decisions,
  /** `true` while the plan describes what WOULD happen; `false` once it has happened. */
  prospective = true,
  /**
   * The order's access state, so an empty plan can be read correctly.
   *
   * WITHOUT IT THIS COMPONENT CRIED CORRUPTION ON A HEALTHY ORDER. An order that has been
   * refunded and revoked legitimately has no active grant rows left, and the empty-plan branch
   * below announced "a ledger this screen cannot read" — which on a settled REFUNDED/REVOKED
   * order reads as data loss. Measured twice on dev, on both refunds of the verification run.
   * The warning is right for a `granted` order with no rows and wrong for a revoked one, and
   * telling those apart needs the state.
   */
  accessState,
}: {
  decisions: readonly WireGrantDecision[];
  prospective?: boolean;
  accessState?: string;
}) {
  const { revoked, retained } = partitionPlan(decisions);
  const columns = itemColumns();

  // AN EMPTY PLAN MEANS TWO OPPOSITE THINGS, and they need two different sentences.
  //
  // On an order whose access has ALREADY been taken back, an empty plan is the expected end
  // state: the grants were revoked, so there is nothing left to list. Saying anything alarming
  // there is a false alarm on a healthy order.
  //
  // On an order still recorded as GRANTED, an empty plan means the grant ledger has nothing for
  // an order that should own rows — which the server refuses a revoke on (`grant_ledger_empty`)
  // rather than treating as "nothing to do". Saying "nothing will be removed" there would be
  // the reassuring version of the same wrong answer.
  if (decisions.length === 0) {
    const settled =
      accessState === 'revoked' || accessState === 'none' || accessState === 'revoke_pending';
    if (settled) {
      return (
        <Banner tone="info">
          Nothing is listed here because this order’s access has already been taken back. The
          grants it created are recorded in the audit trail below.
        </Banner>
      );
    }
    return (
      <Banner tone="warning">
        No grant rows are recorded against this order, so there is nothing here to describe.
        That is not the same as “nothing will be removed” — an order marked as granted with no
        grant rows is a ledger this screen cannot read, and the refund path refuses it rather
        than guessing.
      </Banner>
    );
  }

  return (
    <div className="ops-plan">
      <section className="ops-plan__half">
        <h4>
          {prospective ? 'Will be removed' : 'Removed'} <Badge tone="danger">{revoked.length}</Badge>
        </h4>
        {revoked.length === 0 ? (
          <p className="admin-muted">
            Nothing. Every item on this order is held independently of it.
          </p>
        ) : (
          <DataTable
            columns={columns}
            rows={revoked}
            rowKey={(row) => `revoke:${row.target_kind}:${row.target_key}`}
            empty={null}
          />
        )}
      </section>

      <section className="ops-plan__half">
        <h4>
          {prospective ? 'Will be kept' : 'Kept'} <Badge tone="success">{retained.length}</Badge>
        </h4>
        {retained.length === 0 ? (
          <p className="admin-muted">{OPS_COPY.nothingRetained}</p>
        ) : (
          <>
            <p className="admin-muted">{OPS_COPY.retainedExplained}</p>
            <DataTable
              columns={[
                ...columns,
                {
                  key: 'decision',
                  header: 'Reason',
                  render: (row) => (
                    <Badge tone="success">
                      {RETENTION_LABEL[row.decision] ?? (row.decision || 'Kept')}
                    </Badge>
                  ),
                },
              ]}
              rows={retained}
              rowKey={(row) => `keep:${row.target_kind}:${row.target_key}`}
              empty={null}
            />
          </>
        )}
      </section>
    </div>
  );
}
